// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import DataViewLoadingOverlay from "@store/Library/Components/Toolkit/DataViewLoadingOverlay";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import Typography, { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import constants from "@store/constants";
import { getCustomerDocuments } from "@store/Framework/Services/CustomService";
import { CustomerDocumentModel } from "@store/Framework/Types/Documents";
import File, { FileOffset } from "@store/Library/Components/Icons/File";
import Download, { DownloadOffset } from "@store/Library/Components/Toolkit/Icons/Download";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import CardListHeading from "@store/Overrides/Components/CardListHeading";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { inlineSeparatorStyles } from "@store/Themes/common/mixins/typography";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { IconOffset } from "@toolkit/Framework/Types/Icon";
import { focusStyles, iconScalableStyles, spacingStyles } from "@toolkit/Themes/common/mixins";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
}

export interface MyDocumentsListProps extends WidgetProps {
    fields: {
        [fields.title]: string;
    };
}

export interface MyDocumentsListStyles {
    loadingOverlay?: LoadingOverlayProps;
    wrapper?: InjectableCss;
    errorInfosBox?: InfosBoxProps;
    noResultsText?: TypographyPresentationProps;
    listWrapper?: InjectableCss;
    listItem?: InjectableCss;
    itemWrapper?: LinkPresentationProps;
    itemIcon?: IconPresentationProps;
    itemContent?: InjectableCss;
    itemName?: TypographyPresentationProps;
    itemInfo?: TypographyPresentationProps;
    itemActionWrapper?: InjectableCss;
    itemDownloadAction?: LinkPresentationProps;
}

const getItemActionProps = (src?: IconPresentationProps["src"], offset?: IconOffset): LinkPresentationProps => {
    return {
        icon: {
            position: "left",
            iconProps: {
                src,
                css: css`
                    ${iconScalableStyles(pxToRem(24), offset)}
                    margin-right: ${pxToRem(6)};
                    color: inherit;
                `,
            },
        },
        css: css`
            ${linkActionStyles()}
            ${inlineSeparatorStyles({ spacing: pxToRem(23), color: "secondary" })}
        `,
    };
};

const MyDocumentsListStyles: MyDocumentsListStyles = {
    loadingOverlay: { minHeight: 80 },
    errorInfosBox: { type: "error" },
    noResultsText: typographyGetNoResultsProps(),
    listItem: {
        css: css`
            ${spacingStyles(pxToRem(10), null)}
        `,
    },
    itemWrapper: {
        mergeCss: false,
        css: css`
            ${focusStyles(true)}
            display: block;
            padding: ${pxToRem(16)};
            border: 1px solid ${getColor("common.border")};
            border-radius: ${pxToRem(3)};
            color: ${getColor("text.main")};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            transition: ${transitionHoverStyles("color", "border-color")};

            &:hover,
            &:active {
                border-color: currentColor;
                color: ${getColor("secondary.main")};
            }

            ${breakpoint("lg")} {
                padding: ${pxToRem(22, 20)};
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                align-items: flex-start;
                gap: ${pxToRem(8)};
            `,
        },
    },
    itemIcon: {
        src: File,
        css: css`
            ${iconScalableStyles(pxToRem(24), FileOffset)}
            flex-shrink: 0;
            margin-top: ${pxToRem(1)};
            color: inherit;
        `,
    },
    itemContent: {
        css: css`
            display: flex;
            gap: ${pxToRem(5, 16)};
            flex-wrap: wrap;
            flex-grow: 1;
            align-items: flex-start;

            ${breakpoint("lg")} {
                flex-wrap: nowrap;
            }
        `,
    },
    itemName: {
        css: css`
            display: flex;
            flex-grow: 1;
            width: 100%;
            font-weight: ${x => x.theme.design.typography.weight.semibold};

            ${breakpoint("lg")} {
                width: auto;
            }
        `,
    },
    itemInfo: {
        css: css`
            flex-shrink: 0;
            margin-top: ${pxToRem(1)};
            font-size: ${x => x.theme.design.typography.size.smaller};
            color: ${getColor("text.semi")};
        `,
    },
    itemActionWrapper: {
        css: css`
            display: flex;
            flex-shrink: 0;
        `,
    },
    itemDownloadAction: getItemActionProps(Download, DownloadOffset),
};

const styles = MyDocumentsListStyles;

const MyDocumentsList: FC<MyDocumentsListProps> = ({ fields: { title } }) => {
    const [documents, setDocuments] = useState<CustomerDocumentModel[]>();
    const [isLoading, setIsLoading] = useState(false);
    const [loadComplete, setLoadComplete] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    useEffect(() => {
        setIsLoading(true);
        getCustomerDocuments()
            .then(result => {
                setDocuments(result.customerDocumentCollection.filter(x => x.url));
            })
            .catch(() => {
                setErrorMessage(translate("An error has occurred. Please try again later."));
            })
            .finally(() => {
                setIsLoading(false);
                setLoadComplete(true);
            });
    }, []);

    return (
        <StyledWrapper {...styles.wrapper}>
            <CardListHeading heading={title} />
            <DataViewLoadingOverlay {...styles.loadingOverlay} loading={isLoading} loadComplete={loadComplete}>
                {errorMessage ? (
                    <InfosBox {...styles.errorInfosBox}>{errorMessage}</InfosBox>
                ) : !documents?.length ? (
                    <Typography {...styles.noResultsText}>{translate("No documents available.")}</Typography>
                ) : (
                    <StyledWrapper as="ul" {...styles.listWrapper}>
                        {documents.map(({ name, size, type: baseType, url }) => {
                            const type = baseType.toUpperCase();
                            const infoText = size ? (type ? `${size} ${type}` : size) : type;

                            return (
                                <StyledWrapper key={`${name}_${url}`} as="li" {...styles.listItem}>
                                    <Link {...styles.itemWrapper} href={url} target="blank">
                                        <Icon {...styles.itemIcon} />
                                        <StyledWrapper as="span" {...styles.itemContent}>
                                            <Typography {...styles.itemName}>{name}</Typography>
                                            {Boolean(infoText) && (
                                                <Typography {...styles.itemInfo}>{infoText}</Typography>
                                            )}
                                            <StyledWrapper as="span" {...styles.itemActionWrapper}>
                                                <Link {...styles.itemDownloadAction} tabIndex={-1}>
                                                    {translate("Download")}
                                                </Link>
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </Link>
                                </StyledWrapper>
                            );
                        })}
                    </StyledWrapper>
                )}
            </DataViewLoadingOverlay>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: MyDocumentsList,
    definition: {
        group: "My Account",
        displayName: "My Documents List",
        icon: "list-ul",
        allowedContexts: [constants.pageType.myAccount, constants.pageType.myDocuments],
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Emser Tile Documents",
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
